import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-navy-950">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-navy-950/95 backdrop-blur-sm border-b border-navy-800/50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-mono text-bone-white tracking-widest text-sm font-medium">
            BONESPAY
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-xs text-bone-white/60 hover:text-bone-white transition-colors uppercase tracking-wider"
          >
            <ArrowLeft size={14} />
            Back
          </Link>
        </div>
      </nav>

      <main className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-10">
            <span className="font-mono text-[10px] tracking-widest uppercase text-bone-white/30">
              Legal
            </span>
            <h1 className="font-serif text-bone-white text-3xl md:text-4xl mt-2 mb-3">
              Privacy Policy
            </h1>
            <p className="font-mono text-xs text-bone-white/30">
              Last updated: March 2026
            </p>
            <div className="mt-4 w-full h-px bg-navy-800/50" />
          </div>

          <div className="space-y-8 text-bone-white/70 font-sans text-sm leading-relaxed">
            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                1. Introduction
              </h2>
              <p>
                BonesPay ("we," "our," or "the Platform") is a payment routing platform operated by BONES R&D.
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when
                you use our services, including the BonesPay merchant dashboard, hosted checkout pages,
                WooCommerce plugin, REST API, and any associated services.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                2. Information We Collect
              </h2>
              <h3 className="font-sans text-sm text-bone-white/90 font-medium mb-2">
                2.1 Merchant Account Information
              </h3>
              <p className="mb-3">
                When you register as a merchant, we collect: email address, business name (optional),
                Polygon wallet address(es), and API configuration preferences. We do not collect government-issued
                identification, social security numbers, bank account details, or any KYB (Know Your Business)
                documentation.
              </p>
              <h3 className="font-sans text-sm text-bone-white/90 font-medium mb-2">
                2.2 Transaction Data
              </h3>
              <p className="mb-3">
                For each payment processed through BonesPay, we record: order identifiers, transaction amounts,
                currency information, deposit wallet addresses, transaction hashes (txid), payment status,
                verification results, and timestamps. This data is necessary for on-chain verification,
                dispute resolution, and audit trail purposes.
              </p>
              <h3 className="font-sans text-sm text-bone-white/90 font-medium mb-2">
                2.3 Customer Data
              </h3>
              <p className="mb-3">
                BonesPay minimizes customer data collection. We may process: customer country (derived from
                IP geolocation for provider routing), and order reference identifiers passed by the merchant's
                store. All card details, personal identification, and KYC data are handled exclusively by
                third-party onramp providers (MoonPay, Transak, Stripe Onramp) and are never transmitted to,
                stored on, or accessible by BonesPay systems.
              </p>
              <h3 className="font-sans text-sm text-bone-white/90 font-medium mb-2">
                2.4 Technical Data
              </h3>
              <p>
                We automatically collect: IP addresses, browser type, device information, access timestamps,
                and API usage logs. This data is used for rate limiting, security monitoring, and service
                optimization.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                3. How We Use Your Information
              </h2>
              <ul className="space-y-2 list-none">
                {[
                  'Process and verify payments through on-chain verification on the Polygon blockchain',
                  'Route payments to the optimal onramp provider based on customer geography and order amount',
                  'Deliver HMAC-signed webhook notifications to merchant stores',
                  'Provide real-time payment status updates via the merchant dashboard',
                  'Generate analytics, revenue reports, and provider performance comparisons',
                  'Enforce rate limiting and security controls to prevent fraud and abuse',
                  'Maintain audit trails and callback attempt logs for dispute resolution',
                  'Improve service reliability, routing algorithms, and checkout conversion rates',
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="font-mono text-bone-white/30 flex-shrink-0">&mdash;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                4. Data Storage and Security
              </h2>
              <p className="mb-3">
                All merchant and transaction data is stored in a Supabase-managed PostgreSQL database with
                Row-Level Security (RLS) enabled on every table. RLS policies ensure that merchants can only
                access their own data -- even if the application layer is compromised, the database prevents
                cross-merchant data access.
              </p>
              <p className="mb-3">
                Our security architecture includes 8 independent defense layers: Cloudflare WAF, TLS 1.3
                encryption, rate limiting via Cloudflare Workers KV, API key and HMAC-SHA256 authentication,
                input validation, Row-Level Security, business logic enforcement, and on-chain verification.
              </p>
              <p>
                All API traffic is encrypted via TLS 1.3 with HSTS enforcement. Callback secrets are generated
                using cryptographically secure random bytes (256-bit entropy). All secret comparisons use
                constant-time algorithms to prevent timing attacks.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                5. Blockchain Data
              </h2>
              <p>
                BonesPay verifies payments directly on the Polygon blockchain. Transaction hashes, wallet
                addresses, and USDC transfer amounts are recorded on-chain and are publicly accessible on
                the Polygon blockchain explorer. This is an inherent property of blockchain technology and
                cannot be modified or deleted. Proof-of-payment verification links
                (bonespay.com/verify/HASH) display on-chain verification data publicly by design.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                6. Third-Party Services
              </h2>
              <p className="mb-3">
                BonesPay integrates with the following third-party services:
              </p>
              <ul className="space-y-2 list-none">
                {[
                  'Onramp Providers (MoonPay, Transak, Stripe Onramp) -- handle card processing, KYC, and fiat-to-USDC conversion under their own privacy policies',
                  'Cloudflare -- provides edge hosting, WAF, DDoS protection, and DNS services',
                  'Supabase -- managed database hosting with built-in authentication and real-time capabilities',
                  'Polygon Network (via Alchemy RPC) -- blockchain read operations for payment verification',
                  'Forwarding Service -- temporary wallet generation and USDC forwarding to merchant wallets',
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="font-mono text-bone-white/30 flex-shrink-0">&mdash;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                7. Data Retention
              </h2>
              <p>
                Transaction records and order timelines are retained indefinitely for audit, compliance,
                and dispute resolution purposes. Callback attempt logs are retained for 90 days.
                Merchant account data is retained for as long as the account is active. Upon account
                deletion, merchant profile data is removed, but transaction records may be retained
                as required for legal compliance and on-chain verification integrity.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                8. Your Rights
              </h2>
              <p className="mb-3">
                Depending on your jurisdiction, you may have the right to:
              </p>
              <ul className="space-y-2 list-none">
                {[
                  'Access the personal data we hold about you',
                  'Request correction of inaccurate data',
                  'Request deletion of your account and associated data (subject to retention requirements)',
                  'Export your transaction data in machine-readable format',
                  'Object to processing of your data for analytics purposes',
                  'Withdraw consent where processing is based on consent',
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="font-mono text-bone-white/30 flex-shrink-0">&mdash;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                9. International Data Transfers
              </h2>
              <p>
                BonesPay operates globally across 170+ countries. Data may be processed in any jurisdiction
                where Cloudflare operates edge nodes. Supabase database instances are hosted in the region
                configured during project setup. Blockchain data exists on the global Polygon network and
                is not subject to geographic restrictions.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                10. Cookies and Tracking
              </h2>
              <p>
                BonesPay uses essential cookies for authentication session management on the merchant
                dashboard. We do not use advertising cookies, tracking pixels, or third-party analytics
                services on the checkout page. The hosted checkout is designed to load in under 2 seconds
                with minimal external dependencies.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                11. Changes to This Policy
              </h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this page
                with an updated revision date. Continued use of BonesPay services after changes constitutes
                acceptance of the revised policy.
              </p>
            </section>

            <section>
              <h2 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
                12. Contact
              </h2>
              <p>
                For privacy-related inquiries, data access requests, or concerns about this policy,
                contact BONES R&D at the email address listed in your merchant dashboard.
              </p>
            </section>
          </div>

          <div className="mt-12 border-t border-navy-800/50 pt-6">
            <p className="font-mono text-[10px] text-bone-white/20 uppercase tracking-wider">
              &copy; {new Date().getFullYear()} BonesPay &mdash; A BONES R&D Project.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
